import { useId, useMemo, useRef, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  normalizeCode,
  searchPresetLanguages,
  type PresetLanguage,
} from '@/lib/languages';

interface Props {
  /** Codes already present in the workspace (any casing); shown as disabled. */
  existingCodes: readonly string[];
  /** Called with the chosen preset; parent pre-fills the editable fields. */
  onSelect: (preset: PresetLanguage) => void;
}

/**
 * Hand-rolled accessible combobox (WAI-ARIA combobox + listbox pattern) for the
 * preset language quick-pick. No Radix — house style. Keyboard: type to filter,
 * Up/Down to move, Enter to select, Escape to close. Already-added codes are
 * rendered disabled so they cannot be re-added.
 */
export function LanguagePresetCombobox({ existingCodes, onSelect }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const existing = useMemo(
    () => new Set(existingCodes.map(normalizeCode)),
    [existingCodes],
  );
  const results = useMemo(() => searchPresetLanguages(query), [query]);

  // Index of the first selectable (not-yet-added) option at or after `from`.
  function nextSelectable(from: number, dir: 1 | -1): number {
    for (let i = from; i >= 0 && i < results.length; i += dir) {
      if (!existing.has(normalizeCode(results[i].code))) return i;
    }
    return -1;
  }

  function choose(index: number) {
    const preset = results[index];
    if (!preset || existing.has(normalizeCode(preset.code))) return;
    onSelect(preset);
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      const start = nextSelectable(active + dir, dir);
      if (start !== -1) setActive(start);
    } else if (e.key === 'Enter') {
      if (open && results.length) {
        e.preventDefault();
        choose(active);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    }
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && results[active] ? `${listId}-${active}` : undefined}
          autoComplete="off"
          autoFocus
          placeholder="Search languages — e.g. French, pt-BR, 日本語"
          value={query}
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-surface pl-9 pr-3 py-1 text-sm text-foreground shadow-sm transition-colors',
            'placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
          )}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            const first = nextSelectable(0, 1);
            setActive(first === -1 ? 0 : first);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            // Defer so an option mousedown/click registers before we close.
            blurTimer.current = setTimeout(() => setOpen(false), 120);
          }}
        />
      </div>

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Preset languages"
          className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-surface py-1 shadow-lg"
        >
          {results.length === 0 && (
            <li className="px-3 py-2 text-xs text-muted-foreground" role="presentation">
              No match — switch to “Custom language” to enter it by hand.
            </li>
          )}
          {results.map((lang, i) => {
            const added = existing.has(normalizeCode(lang.code));
            const isActive = i === active;
            return (
              // Keyboard navigation is handled on the combobox input via
              // aria-activedescendant (WAI-ARIA listbox pattern), so the option
              // itself needs no key handler.
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={lang.code}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={isActive}
                aria-disabled={added || undefined}
                className={cn(
                  'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
                  isActive && !added && 'bg-muted',
                  added && 'cursor-not-allowed opacity-50',
                )}
                onMouseEnter={() => !added && setActive(i)}
                onMouseDown={(e) => {
                  // Prevent input blur from firing before the click handler.
                  e.preventDefault();
                  if (blurTimer.current) clearTimeout(blurTimer.current);
                }}
                onClick={() => choose(i)}
              >
                <span className="flex-1 truncate">
                  <span className="font-medium text-text-primary">{lang.label}</span>{' '}
                  <span className="text-muted-foreground">· {lang.native}</span>
                </span>
                <code className="shrink-0 text-[11px] text-muted-foreground">{lang.code}</code>
                {added && (
                  <span className="inline-flex shrink-0 items-center gap-0.5 text-[11px] text-muted-foreground">
                    <Check className="size-3" aria-hidden /> Added
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
